// Funções de rastreamento para Google Analytics 4, Google Ads, GTM e Meta Pixel

import { TRACKING_CONFIG } from '../config/tracking';

const ensureDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

const loadGtagScript = (measurementId) => {
  if (!measurementId) return;
  const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`);
  if (existingScript) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
};

const ensureGtag = () => {
  ensureDataLayer();

  if (!window.gtag) {
    window.gtag = function gtag(){
      window.dataLayer.push(arguments);
    };
  }

  return window.gtag;
};

const pushToDataLayer = (eventName, params = {}) => {
  ensureDataLayer().push({
    event: eventName,
    ...params
  });
};

const sendGA4Event = (eventName, params = {}) => {
  if (!TRACKING_CONFIG.ga4.enabled || !window.gtag) return;
  window.gtag('event', eventName, params);
};

// ==================== GOOGLE ANALYTICS 4 ====================

export const initGA4 = () => {
  if (!TRACKING_CONFIG.ga4.enabled) return;

  const measurementId = TRACKING_CONFIG.ga4.measurementId;
  if (!measurementId) return;

  loadGtagScript(measurementId);
  const gtag = ensureGtag();

  gtag('js', new Date());
  gtag('config', measurementId, {
    send_page_view: true,
    anonymize_ip: true
  });

  console.log('✅ GA4 initialized:', measurementId);
};

// ==================== GOOGLE ADS ====================

export const initGoogleAds = () => {
  if (!TRACKING_CONFIG.googleAds.enabled) return;

  const measurementId = TRACKING_CONFIG.googleAds.measurementId;
  if (!measurementId) return;

  loadGtagScript(measurementId);
  const gtag = ensureGtag();

  gtag('js', new Date());
  gtag('config', measurementId);

  console.log('✅ Google Ads initialized:', measurementId);
};

export const trackGoogleAdsConversion = (conversionLabel, value = 1.0) => {
  if (!TRACKING_CONFIG.googleAds.enabled || !window.gtag || !conversionLabel) return;

  window.gtag('event', 'conversion', {
    send_to: conversionLabel,
    value,
    currency: 'BRL'
  });

  console.log('📊 Google Ads Conversion:', conversionLabel);
};

// ==================== META PIXEL ====================

export const initMetaPixel = () => {
  if (!TRACKING_CONFIG.metaPixel.enabled) return;

  const pixelId = TRACKING_CONFIG.metaPixel.pixelId;
  if (!pixelId) return;

  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');

  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');

  console.log('✅ Meta Pixel initialized:', pixelId);
};

export const trackMetaPixelEvent = (eventName, params = {}) => {
  if (!TRACKING_CONFIG.metaPixel.enabled || !window.fbq) return;

  window.fbq('track', eventName, params);
  console.log('📊 Meta Pixel Event:', eventName, params);
};

// ==================== EVENTOS ESPECÍFICOS ====================

export const trackWhatsAppClick = () => {
  const params = {
    link_type: 'whatsapp',
    contact_channel: 'whatsapp',
    page_location: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
    outbound: true
  };

  pushToDataLayer('whatsapp_click', params);
  sendGA4Event('whatsapp_click', params);
  sendGA4Event('generate_lead', {
    ...params,
    method: 'whatsapp'
  });

  if (TRACKING_CONFIG.googleAds.enabled) {
    trackGoogleAdsConversion(
      TRACKING_CONFIG.googleAds.conversionLabels.whatsappClick
    );
  }

  if (TRACKING_CONFIG.metaPixel.enabled) {
    trackMetaPixelEvent(TRACKING_CONFIG.metaPixel.events.whatsappClick, {
      content_name: 'WhatsApp Contact',
      content_category: 'Contact'
    });
  }

  console.log('📱 WhatsApp click tracked');
};

export const trackPhoneClick = () => {
  const params = {
    link_type: 'phone',
    contact_channel: 'phone',
    page_location: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
    outbound: true
  };

  pushToDataLayer('phone_click', params);
  sendGA4Event('phone_click', params);
  sendGA4Event('generate_lead', {
    ...params,
    method: 'phone'
  });

  if (TRACKING_CONFIG.googleAds.enabled) {
    trackGoogleAdsConversion(
      TRACKING_CONFIG.googleAds.conversionLabels.phoneClick
    );
  }

  if (TRACKING_CONFIG.metaPixel.enabled) {
    trackMetaPixelEvent(TRACKING_CONFIG.metaPixel.events.phoneClick, {
      content_name: 'Phone Contact',
      content_category: 'Contact'
    });
  }

  console.log('📞 Phone click tracked');
};

export const trackSectionView = (sectionName) => {
  const params = {
    section_name: sectionName,
    page_location: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title
  };

  pushToDataLayer('section_view', params);
  sendGA4Event('section_view', params);
  sendGA4Event('view_item', {
    item_name: sectionName,
    item_category: 'section'
  });

  if (TRACKING_CONFIG.metaPixel.enabled) {
    trackMetaPixelEvent('ViewContent', {
      content_name: sectionName,
      content_type: 'section'
    });
  }

  console.log('👁️ Section viewed:', sectionName);
};

export const trackLead = () => {
  const params = {
    lead_type: 'landing_page_contact',
    page_location: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title
  };

  pushToDataLayer('lead_generated', params);
  sendGA4Event('lead_generated', params);
  sendGA4Event('generate_lead', params);

  if (TRACKING_CONFIG.metaPixel.enabled) {
    trackMetaPixelEvent('Lead', {
      content_name: 'Landing Page Lead',
      value: 1.0,
      currency: 'BRL'
    });
  }

  console.log('🎯 Lead tracked');
};

// ==================== GOOGLE TAG MANAGER (OPCIONAL) ====================

export const initGoogleTagManager = () => {
  if (!TRACKING_CONFIG.googleTagManager.enabled) return;

  const containerId = TRACKING_CONFIG.googleTagManager.containerId;
  if (!containerId) return;

  if (document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${containerId}"]`)) {
    return;
  }

  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer', containerId);

  console.log('✅ GTM initialized:', containerId);
};
